import { useEffect, useRef } from 'react';

const oldSteps = [
  { step: 1, label: 'Roof Cap에 볼트 체결' },
  { step: 2, label: '캡 뚜껑 닫기' },
  { step: 3, label: 'Roof Cap 주변 실리콘 도포', sub: '실리콘건으로 수작업', highlight: true },
  { step: 4, label: '실리콘 경화 대기', sub: '24시간 이상', highlight: true },
];

const newSteps = [
  { step: 1, label: 'Smart Cap에 볼트 체결', sub: '실리콘 패킹 자동 압착' },
  { step: 2, label: '캡 뚜껑 닫기 → 끝!', sub: '끝!', isFinal: true },
];

const tableRows = [
  {
    feature: '시공 단계',
    icon: 'ri-list-check',
    smart: '2단계 (체결(패킹 자동 압착)→뚜껑닫기→끝!)',
    existing: '4단계 (체결→뚜껑닫기→실리콘 도포→경화 대기)',
  },
  {
    feature: '실리콘 작업',
    icon: 'ri-drop-fill',
    smart: '완전 불필요',
    existing: '반드시 필요',
  },
  {
    feature: '경화 대기',
    icon: 'ri-time-line',
    smart: '불필요, 즉시 방수',
    existing: '24시간 이상 경화 대기',
  },
  {
    feature: '개당 절감액',
    icon: 'ri-coin-fill',
    smart: '350원 절감 (인건비300+재료비50)',
    existing: '추가 비용 발생',
  },
  {
    feature: '방수 내구성',
    icon: 'ri-shield-star-fill',
    smart: '5년 이상 (실리콘 패킹 자가복원)',
    existing: '1~2년 후 실리콘 균열·박리',
  },
  {
    feature: '자외선 내구성',
    icon: 'ri-sun-fill',
    smart: '엔지니어링 플라스틱 (열화 강함)',
    existing: '일반 플라스틱 (2~3년 내 파손)',
  },
  {
    feature: '캡 파손 위험',
    icon: 'ri-error-warning-fill',
    smart: '고강도 소재, 파손 없음',
    existing: '열화로 약화·부서짐',
  },
  {
    feature: 'A/S 비용',
    icon: 'ri-customer-service-2-fill',
    smart: '대폭 감소',
    existing: '건당 15~40만원 반복 발생',
  },
  {
    feature: '시공자 의존도',
    icon: 'ri-tools-fill',
    smart: '누구나 균일 품질',
    existing: '실리콘 도포 숙련도에 의존',
  },
];

const ComparisonSection = () => {
  const titleRef = useRef<HTMLDivElement>(null);
  const leftRef = useRef<HTMLDivElement>(null);
  const rightRef = useRef<HTMLDivElement>(null);
  const bannerRef = useRef<HTMLDivElement>(null);
  const tableRef = useRef<HTMLDivElement>(null);
  const rowRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('opacity-100', 'translate-y-0');
          }
        });
      },
      { threshold: 0.1 }
    );
    [titleRef, leftRef, rightRef, bannerRef, tableRef].forEach(
      (r) => r.current && observer.observe(r.current)
    );
    rowRefs.current.forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section id="comparison" className="py-24 bg-[#F8F9FC]">
      <div className="max-w-5xl mx-auto px-6 lg:px-8">

        {/* ── 타이틀 ── */}
        <div
          ref={titleRef}
          className="text-center mb-14 opacity-0 translate-y-6 transition-all duration-700"
        >
          <span className="text-[#FF6B35] text-sm font-semibold tracking-widest uppercase">Process Comparison</span>
          <h2 className="mt-3 text-3xl lg:text-4xl font-bold text-[#1B2A4A] font-display">
            시공 프로세스 비교
          </h2>
          <p className="mt-4 text-gray-500 text-base max-w-xl mx-auto">
            단계 수가 곧 시간이고, 비용입니다
          </p>
          <div className="mt-4 w-12 h-1 bg-[#FF6B35] mx-auto rounded-full" />
        </div>

        {/* ── 좌우 스텝 비교 ── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

          {/* 왼쪽: 기존 Roof Cap */}
          <div
            ref={leftRef}
            className="opacity-0 translate-y-8 transition-all duration-700 delay-100 bg-white rounded-2xl border border-red-100 overflow-hidden"
          >
            <div className="bg-gradient-to-r from-red-500 to-gray-600 px-6 py-5 flex items-center gap-3">
              <div className="w-9 h-9 flex items-center justify-center rounded-full bg-white/20">
                <i className="ri-tools-fill text-white text-lg" />
              </div>
              <div>
                <p className="text-white/70 text-xs font-medium tracking-widest uppercase">기존 방식</p>
                <h3 className="text-white font-bold text-lg leading-tight">기존 Roof Cap 방식</h3>
              </div>
              <span className="ml-auto bg-white/20 text-white text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap">4단계</span>
            </div>
            <div className="px-6 py-6 space-y-0">
              {oldSteps.map((s, i) => (
                <div key={s.step} className="flex gap-4">
                  <div className="flex flex-col items-center">
                    <div className={`w-8 h-8 flex items-center justify-center rounded-full text-xs font-bold shrink-0 ${s.highlight ? 'bg-red-500 text-white' : 'bg-gray-200 text-gray-600'}`}>
                      {s.step}
                    </div>
                    {i < oldSteps.length - 1 && <div className="w-0.5 h-8 bg-gray-200 my-1" />}
                  </div>
                  <div className="pb-2 pt-1">
                    <span className="text-[10px] font-bold tracking-widest uppercase text-gray-400">STEP {s.step}</span>
                    <p className={`font-semibold text-sm leading-snug mt-0.5 ${s.highlight ? 'text-red-600' : 'text-[#1B2A4A]'}`}>{s.label}</p>
                    {s.sub && (
                      <p className="text-red-400 text-xs mt-0.5 flex items-center gap-1">
                        <i className="ri-alert-fill text-xs" />{s.sub}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div className="mx-6 mb-6 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
              <p className="text-red-600 text-sm font-semibold text-center leading-relaxed">
                총 4단계 &nbsp;·&nbsp; 실리콘 공정 필수 &nbsp;·&nbsp; 경화 대기 시간 발생
              </p>
            </div>
          </div>

          {/* 오른쪽: Smart Cap */}
          <div
            ref={rightRef}
            className="opacity-0 translate-y-8 transition-all duration-700 delay-200 bg-white rounded-2xl border border-green-100 overflow-hidden"
          >
            <div className="bg-gradient-to-r from-green-500 to-[#FF6B35] px-6 py-5 flex items-center gap-3">
              <div className="w-9 h-9 flex items-center justify-center rounded-full bg-white/20">
                <i className="ri-shield-check-fill text-white text-lg" />
              </div>
              <div>
                <p className="text-white/70 text-xs font-medium tracking-widest uppercase">스마트캡 방식</p>
                <h3 className="text-white font-bold text-lg leading-tight">Smart Cap 방식</h3>
              </div>
              <span className="ml-auto bg-white/20 text-white text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap">2단계</span>
            </div>
            <div className="px-6 py-6 space-y-0">
              {newSteps.map((s, i) => (
                <div key={s.step} className="flex gap-4">
                  <div className="flex flex-col items-center">
                    <div className={`w-8 h-8 flex items-center justify-center rounded-full text-xs font-bold shrink-0 ${s.isFinal ? 'bg-[#FF6B35] text-white' : 'bg-green-500 text-white'}`}>
                      {s.step}
                    </div>
                    {i < newSteps.length - 1 && <div className="w-0.5 h-8 bg-green-100 my-1" />}
                  </div>
                  <div className="pb-2 pt-1">
                    <span className="text-[10px] font-bold tracking-widest uppercase text-gray-400">STEP {s.step}</span>
                    <p className="font-semibold text-sm text-[#1B2A4A] leading-snug mt-0.5">{s.label}</p>
                    {s.sub && (
                      <p className={`text-xs mt-0.5 flex items-center gap-1 ${s.isFinal ? 'text-[#FF6B35] font-bold' : 'text-green-600'}`}>
                        {s.isFinal
                          ? <><i className="ri-checkbox-circle-fill" />{s.sub}</>
                          : <><i className="ri-check-line" />{s.sub}</>
                        }
                      </p>
                    )}
                  </div>
                </div>
              ))}
              <div className="h-14" />
            </div>
            <div className="mx-6 mb-6 bg-green-50 border border-green-200 rounded-xl px-4 py-3">
              <p className="text-green-700 text-sm font-semibold text-center leading-relaxed">
                총 2단계 &nbsp;·&nbsp; 실리콘 공정 완전 삭제 &nbsp;·&nbsp; 경화 대기 불필요
              </p>
            </div>
          </div>
        </div>

        {/* ── 강조 배너 ── */}
        <div
          ref={bannerRef}
          className="opacity-0 translate-y-6 transition-all duration-700 delay-300 mt-6"
        >
          <div className="relative bg-[#1B2A4A] rounded-2xl px-6 py-6 flex flex-col sm:flex-row items-center justify-center gap-4 overflow-hidden">
            <div className="absolute left-0 top-0 w-40 h-full bg-[#FF6B35]/10 rounded-2xl" />
            <div className="absolute right-0 top-0 w-40 h-full bg-green-500/10 rounded-2xl" />
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#FF6B35]/20 flex-shrink-0 z-10">
              <i className="ri-flashlight-fill text-[#FF6B35] text-2xl" />
            </div>
            <p className="z-10 text-center sm:text-left">
              <span className="text-white font-bold text-xl lg:text-2xl">2단계 공정 삭제</span>
              <span className="text-gray-400 text-xl lg:text-2xl mx-3">=</span>
              <span className="text-[#FF6B35] font-bold text-xl lg:text-2xl">시공 속도 50% 향상</span>
            </p>
            <div className="z-10 hidden sm:flex items-center gap-1.5 bg-green-500/20 border border-green-500/30 px-4 py-2 rounded-full flex-shrink-0">
              <i className="ri-rocket-2-fill text-green-400 text-sm" />
              <span className="text-green-400 text-sm font-bold whitespace-nowrap">검증된 현장 데이터</span>
            </div>
          </div>
        </div>

        {/* ── 상세 비교 테이블 ── */}
        <div
          ref={tableRef}
          className="mt-16 opacity-0 translate-y-6 transition-all duration-700 delay-100"
        >
          {/* 테이블 타이틀 */}
          <div className="text-center mb-8">
            <span className="text-[#FF6B35] text-sm font-semibold tracking-widest uppercase">Detail Comparison</span>
            <h3 className="mt-2 text-2xl lg:text-3xl font-bold text-[#1B2A4A] font-display">
              항목별 상세 비교
            </h3>
            <div className="mt-3 w-10 h-1 bg-[#FF6B35] mx-auto rounded-full" />
          </div>

          {/* 컬럼 헤더 */}
          <div className="grid grid-cols-3 mb-2 px-2">
            <div />
            <div className="text-center">
              <span className="relative inline-flex items-center gap-1.5 bg-[#FF6B35] text-white text-xs font-bold px-4 py-1.5 rounded-full">
                <i className="ri-shield-check-fill" />
                Smart Cap
                <span className="ml-1 bg-white/25 text-white text-[10px] px-1.5 py-0.5 rounded-full">추천</span>
              </span>
            </div>
            <div className="text-center">
              <span className="inline-flex items-center gap-1.5 bg-gray-300 text-gray-600 text-xs font-semibold px-4 py-1.5 rounded-full">
                <i className="ri-contrast-2-fill" />
                기존 Roof Cap
              </span>
            </div>
          </div>

          {/* 테이블 본문 */}
          <div className="bg-white rounded-2xl overflow-hidden border border-gray-100">
            {tableRows.map((row, i) => (
              <div
                key={i}
                ref={(el) => { rowRefs.current[i] = el; }}
                className={`grid grid-cols-3 border-b border-gray-100 last:border-0
                  hover:bg-[#FF6B35]/3 transition-colors duration-200
                  opacity-0 translate-y-3 transition-[opacity,transform] duration-500
                  ${i % 2 === 1 ? 'bg-[#FAFBFC]' : 'bg-white'}`}
                style={{ transitionDelay: `${i * 60}ms` }}
              >
                {/* 항목명 */}
                <div className="px-5 py-4 flex items-center gap-2.5">
                  <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-[#1B2A4A]/6 flex-shrink-0">
                    <i className={`${row.icon} text-sm text-[#1B2A4A]`} />
                  </div>
                  <span className="font-semibold text-[#1B2A4A] text-sm leading-snug">{row.feature}</span>
                </div>

                {/* Smart Cap */}
                <div className="px-4 py-4 flex items-center justify-center border-l border-gray-100 bg-[#FF6B35]/4">
                  <span className="inline-flex items-start gap-1.5 text-[#1B2A4A] font-semibold text-sm leading-snug">
                    <span className="w-5 h-5 flex items-center justify-center rounded-full bg-[#FF6B35] flex-shrink-0 mt-0.5">
                      <i className="ri-check-line text-white text-xs" />
                    </span>
                    {row.smart}
                  </span>
                </div>

                {/* 기존 Roof Cap */}
                <div className="px-4 py-4 flex items-center justify-center border-l border-gray-100">
                  <span className="inline-flex items-start gap-1.5 text-gray-400 text-sm leading-snug">
                    <span className="w-5 h-5 flex items-center justify-center rounded-full bg-gray-200 flex-shrink-0 mt-0.5">
                      <i className="ri-close-line text-gray-400 text-xs" />
                    </span>
                    {row.existing}
                  </span>
                </div>
              </div>
            ))}
          </div>

          <p className="text-center text-gray-400 text-xs mt-4">
            ※ 위 비교는 일반적인 현장 사용 조건 및 업계 평균 데이터를 기준으로 작성되었습니다.
          </p>
        </div>

      </div>
    </section>
  );
};

export default ComparisonSection;
